import { gql } from 'apollo-angular';
import { DeepLink } from '../../../shared-module/shared/entities/notification/notification';
import { NotificationChannelKey } from '../notification-settings-list/graphql';

// #737: Support-Flaeche fuer Zustellprobleme (Notification-Diagnose).
// Alle Operationen pruefen serverseitig selbst die Rolle framework_notifications_support
// (requireSupportRole()) — das Web-Gating ueber PermissionGuard ist Komfort, keine Sicherheitsgrenze.
//
// NotificationChannelKey (#735) und DeepLink (#736) werden wiederverwendet und hier bewusst NICHT
// erneut deklariert. NotificationChannelKey wird re-exportiert, damit Komponenten und Tests dieser
// Flaeche nur aus einer Datei importieren muessen.
export { NotificationChannelKey };

/**
 * Zustellstatus einer einzelnen Delivery.
 *
 * Kommt als roher String aus dem Schema (`status: String!`) und wird serverseitig mit
 * `DeliveryStatus.valueOf(status)` geparst — also CASE-SENSITIV. Der Statusfilter darf ausschliesslich
 * diese Werte senden; ein Freitextfeld waere eine Fehlerquelle.
 */
export enum DeliveryStatus {
  PENDING = 'PENDING',
  SENDING = 'SENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED'
}

/** Warum die Engine so entschieden hat. Roher Enum-Name, vom Backend NICHT uebersetzt. */
export enum DeliveryDecisionReason {
  FORCED = 'FORCED',
  TYPE_DEFAULT = 'TYPE_DEFAULT',
  USER_OPT_IN = 'USER_OPT_IN',
  USER_OPT_OUT = 'USER_OPT_OUT',
  NO_EMAIL_ADDRESS = 'NO_EMAIL_ADDRESS',
  NO_DEVICE = 'NO_DEVICE',
  USER_DEACTIVATED = 'USER_DEACTIVATED',
  /** Empfaenger ohne Voll-Lizenz ist fuer diesen Typ nicht adressierbar. */
  NO_FULL_LICENCE = 'NO_FULL_LICENCE',
  CHANNEL_DISABLED = 'CHANNEL_DISABLED'
}

/**
 * Eine Audit-Zeile pro Zustellversuch.
 *
 * ACHTUNG: `id` ist die DELIVERY-ID, `notificationId` die der Eltern-Notification. Der Drilldown auf
 * das Detail geht ueber `notificationId` — `getNotificationAdminDetail(id)` mit der Delivery-ID
 * liefert immer null.
 */
export interface NotificationDeliveryAuditEntry {
  id: string;
  notificationId: string;
  typeKey: string;
  recipientUserId: string;
  channel: NotificationChannelKey;
  status: string;
  decisionReason: string;
  attempts: number;
  lastError: string | null;
  notBefore: string | null;
  sentAt: string | null;
  /** Wann der Empfaenger ueber DIESEN Kanal darauf gestossen ist. Leer = zugestellt, nie geoeffnet. */
  openedAt: string | null;
  createdAt: string;
}

/** Cursor-Seite ueber UUIDv7, sortiert id DESC. Kein totalCount — bewusst, siehe Spec. */
export interface NotificationDeliveryAuditPage {
  items: NotificationDeliveryAuditEntry[];
  nextCursor: string | null;
}

/**
 * Filter der Liste. Im Schema NON-NULL: bei leerer Filterleiste geht `{}` ueber die Leitung,
 * niemals null oder undefined.
 *
 * `from`/`to` muessen ISO-8601-Instants mit `Z` sein (serverseitig `Instant.parse`), beide Grenzen
 * inklusiv, und sie filtern auf `createdAt` — nicht auf `sentAt`.
 */
export interface NotificationDeliveryAuditFilter {
  recipientUserId?: string;
  channel?: NotificationChannelKey;
  status?: string;
  typeKey?: string;
  from?: string;
  to?: string;
}

/** Zustand eines Kanals, wie ihn die Nachrichtenliste als Zusammenfassung ihrer Kinder bekommt. */
export interface NotificationAdminChannelState {
  channel: NotificationChannelKey;
  status: string;
  decisionReason: string;
}

/**
 * Eine Zeile der Nachrichtenliste — die ELTERN-Ebene.
 *
 * ACHTUNG: `id` ist hier die NOTIFICATION-ID. Der Drilldown geht direkt darueber, anders als bei
 * NotificationDeliveryAuditEntry, wo `id` die Delivery-ID ist und der Umweg ueber notificationId
 * noetig ist.
 *
 * Traegt bewusst weder Titel noch Text: der Titel ist bei den meisten Typen der Typ in Prosa
 * („Neue Aufgabe zugewiesen" bei jedem task.assigned). Zwei Zeilen desselben Typs an denselben
 * Empfaenger sind verschiedene Nachrichten, keine Dopplung.
 */
export interface NotificationAdminEntry {
  id: string;
  recipientUserId: string;
  typeKey: string;
  readAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  channels: NotificationAdminChannelState[];
}

/**
 * Filter der Nachrichtenliste. Im Schema NON-NULL: bei leerer Filterleiste geht `{}` ueber die
 * Leitung.
 *
 * KEIN channel und KEIN status — auf einer Nachricht hiesse „Status: FAILED" nur „bei irgendeinem
 * Kanal ging etwas schief" und traefe auch Nachrichten, die der Empfaenger laengst hat. Wer so
 * filtern will, wechselt in den Zustell-Tab.
 *
 * from/to muessen ISO-8601-Instants mit `Z` sein, beide Grenzen inklusiv, gefiltert wird auf
 * createdAt.
 */
export interface NotificationAdminFilter {
  recipientUserId?: string;
  typeKey?: string;
  from?: string;
  to?: string;
}

export interface NotificationAdminPage {
  items: NotificationAdminEntry[];
  nextCursor: string | null;
}

export interface NotificationAdminPageResponse {
  getNotificationAdminPage: NotificationAdminPage;
}

/**
 * Ohne groupKey und expiresAt: beide liefert der Vertrag mit, aber groupKey ist ein Identifikator
 * und kein Anzeigewert, und expiresAt ist immer createdAt plus Aufbewahrungsfrist. Beide stehen im
 * Detail.
 */
export const NOTIFICATION_ADMIN_PAGE_QUERY = gql`
  query getNotificationAdminPage($filter: NotificationAdminFilter!, $cursor: ID, $limit: Int!) {
    getNotificationAdminPage(filter: $filter, cursor: $cursor, limit: $limit) {
      items {
        id
        recipientUserId
        typeKey
        readAt
        deletedAt
        createdAt
        channels {
          channel
          status
          decisionReason
        }
      }
      nextCursor
    }
  }
`;

export interface NotificationAdminDetail {
  id: string;
  recipientUserId: string;
  typeKey: string;
  title: string;
  body: string | null;
  contextPayload: Record<string, unknown>;
  groupKey: string | null;
  deepLink: DeepLink | null;
  readAt: string | null;
  /** Vom Empfaenger aus seiner Liste entfernt. Leer = steht noch drin. Setzt readAt mit. */
  deletedAt: string | null;
  createdAt: string;
  expiresAt: string;
  deliveries: NotificationDeliveryAuditEntry[];
}

export interface DeliveryAuditPageResponse {
  getNotificationDeliveryAuditPage: NotificationDeliveryAuditPage;
}

/** null ist ein legitimes Ergebnis: die Notification kann durch die TTL bereits geloescht sein. */
export interface AdminDetailResponse {
  getNotificationAdminDetail: NotificationAdminDetail | null;
}

export interface RequeueResponse {
  requeueFailedNotificationDeliveries: number;
}

const DELIVERY_FIELDS = `
  id
  notificationId
  typeKey
  recipientUserId
  channel
  status
  decisionReason
  attempts
  lastError
  notBefore
  sentAt
  openedAt
  createdAt
`;

/** Standard-limit 50; der Server klemmt still bei 100 (MAX_LIMIT). */
export const DELIVERY_AUDIT_PAGE_QUERY = gql`
  query getNotificationDeliveryAuditPage($filter: NotificationDeliveryAuditFilter!, $cursor: ID, $limit: Int!) {
    getNotificationDeliveryAuditPage(filter: $filter, cursor: $cursor, limit: $limit) {
      items {
        ${DELIVERY_FIELDS}
      }
      nextCursor
    }
  }
`;

export const NOTIFICATION_ADMIN_DETAIL_QUERY = gql`
  query getNotificationAdminDetail($id: ID!) {
    getNotificationAdminDetail(id: $id) {
      id
      recipientUserId
      typeKey
      title
      body
      contextPayload
      groupKey
      deepLink {
        type
        id
      }
      readAt
      deletedAt
      createdAt
      expiresAt
      deliveries {
        ${DELIVERY_FIELDS}
      }
    }
  }
`;

/**
 * Abo-Einsicht fuer einen FREMDEN Nutzer. Liefert denselben Katalog wie die eigenen
 * Einstellungen (#735), aber schreibgeschuetzt — es gibt keine Mutation, die Praeferenzen fuer
 * Fremde setzt, und das ist Absicht.
 */
export const NOTIFICATION_CATALOG_FOR_USER_QUERY = gql`
  query getNotificationCatalogForUser($userId: ID!, $locale: String) {
    catalog: getNotificationCatalogForUser(userId: $userId, locale: $locale) {
      typeKey
      label
      categoryKey
      categoryLabel
      sortOrder
      channels {
        channel
        policy
        effectiveEnabled
        editable
      }
    }
  }
`;

/**
 * ACHTUNG: nimmt denselben Filtertyp wie die Liste, wertet aber NUR recipientUserId, channel und
 * typeKey aus. `status`, `from` und `to` werden serverseitig verworfen — FAILED steckt im Namen der
 * Mutation und wird erzwungen, der Zeitraum nicht. Der Bestaetigungsdialog muss das benennen.
 */
export const REQUEUE_FAILED_DELIVERIES_MUTATION = gql`
  mutation requeueFailedNotificationDeliveries($filter: NotificationDeliveryAuditFilter!) {
    requeueFailedNotificationDeliveries(filter: $filter)
  }
`;
